import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useStore } from './store';
import { OWNER_EMAIL } from './lib/owner';
import { supabase, DEMO_MODE } from './lib/supabase';
import { calcPropFirmStats } from './lib/propfirm';
import { categorizeAccount } from './lib/accounts';
import { useToast, ToastContainer } from './components/Toast';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import ProductTour from './components/ProductTour';
import CookieConsent from './components/CookieConsent';
import TradovateReconnectModal from './components/TradovateReconnectModal';
import { TRADOVATE_SYNC_ENABLED } from './lib/brokerFlags';
import UpgradePrompt from './components/UpgradePrompt';
import InstallPrompt from './components/InstallPrompt';
import { canUse, type Feature } from './lib/featureGates';

// Code-split — kept out of the landing/auth critical bundle, loaded on demand.
const Layout = lazy(() => import('./components/Layout'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TradesList = lazy(() => import('./pages/TradesList'));
const Reports = lazy(() => import('./pages/Reports'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Settings = lazy(() => import('./pages/Settings'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Accessibility = lazy(() => import('./pages/Accessibility'));
const RiskDisclosure = lazy(() => import('./pages/RiskDisclosure'));
const Guides = lazy(() => import('./pages/Guides'));
const PropHQ = lazy(() => import('./pages/PropHQ'));
const BusinessManager = lazy(() => import('./pages/BusinessManager'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const PropFirms = lazy(() => import('./pages/PropFirms'));
// const Alternatives = lazy(() => import('./pages/Alternatives')); // unpublished — see route note below
const TradeReplay = lazy(() => import('./pages/TradeReplay'));
const PlaybookLibrary = lazy(() => import('./pages/PlaybookLibrary'));
const SetupDetail = lazy(() => import('./pages/SetupDetail'));
const DayRecap = lazy(() => import('./pages/DayRecap'));
const MarketingAgent = lazy(() => import('./pages/MarketingAgent'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Coach = lazy(() => import('./pages/Coach'));
const StrategyAnalyzer = lazy(() => import('./pages/StrategyAnalyzer'));
const StrategyMiner = lazy(() => import('./pages/StrategyMiner'));
const EmotionJournal = lazy(() => import('./pages/EmotionJournal'));
const CopyTrader = lazy(() => import('./pages/CopyTrader'));
const EconomicCalendar = lazy(() => import('./pages/EconomicCalendar'));
const Students = lazy(() => import('./pages/Students'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const Redeem = lazy(() => import('./pages/Redeem'));

/** Redirects /dashboard/replay → /dashboard/replay/:mostRecentTradeId */
function ReplayRedirect() {
  const { trades } = useStore();
  const navigate = useNavigate();
  useEffect(() => {
    if (!trades.length) return;
    const sorted = [...trades].sort(
      (a, b) =>
        b.trade_date.localeCompare(a.trade_date) ||
        (b.entry_time ?? '').localeCompare(a.entry_time ?? ''),
    );
    navigate(`/dashboard/replay/${sorted[0].id}`, { replace: true });
  }, [trades, navigate]);
  return null;
}

/* מסך טעינה קצר — רק לבדיקת session ראשונית */
function SplashScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0a1230 0%, #13113a 55%, #0d1f3c 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 32,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* decorative blobs */}
      <div
        style={{
          position: 'absolute',
          width: 480,
          height: 480,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,91,255,0.18) 0%, transparent 70%)',
          top: -120,
          right: -120,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(34,211,238,0.10) 0%, transparent 70%)',
          bottom: -80,
          left: -80,
          pointerEvents: 'none',
        }}
      />

      {/* logo */}
      <img
        src="/logo-sm.webp"
        alt="TraderYo"
        style={{
          width: 200,
          height: 'auto',
          objectFit: 'contain',
          position: 'relative',
          zIndex: 1,
        }}
      />

      {/* gradient progress bar */}
      <div
        style={{
          width: 120,
          height: 3,
          borderRadius: 999,
          background: 'rgba(255,255,255,0.08)',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1,
        }}
      >
        <style>{`
          @keyframes slide { 0%{transform:translateX(-100%)} 100%{transform:translateX(200%)} }
        `}</style>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, #a78bfa, #635bff, #22d3ee)',
            borderRadius: 999,
            animation: 'slide 1.2s ease-in-out infinite',
          }}
        />
      </div>
    </div>
  );
}

function AuthListener({ onReady }: { onReady: (hasUser: boolean) => void }) {
  const { setUser, loadDataInBackground } = useStore();
  const navigate = useNavigate();
  const handledRef = useRef(false);

  const handleUser = (u: {
    id: string;
    email?: string;
    user_metadata?: Record<string, string>;
  }) => {
    if (handledRef.current) return;
    handledRef.current = true;

    const name = u.user_metadata?.full_name ?? u.email ?? 'User';
    const email = u.email ?? '';

    // 1. עדכן user מיד — פתח את האפליקציה
    setUser({ id: u.id, name, email });
    onReady(true);
    // Don't navigate away if already on a dashboard page —
    // mobile OAuth redirect lands on /dashboard/settings?tv_code=... and we must preserve that URL.
    if (!window.location.pathname.startsWith('/dashboard')) {
      navigate('/dashboard', { replace: true });
    }

    // 2. טען נתונים מ-Supabase ברקע — בלי לחסום
    loadDataInBackground(u.id, name, email);
  };

  useEffect(() => {
    if (DEMO_MODE || !supabase) {
      onReady(false);
      return;
    }

    // timeout קצר — 3 שניות לבדיקת session בלבד
    const timer = setTimeout(() => {
      onReady(false);
    }, 3000);

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        clearTimeout(timer);
        if (session?.user) {
          handleUser(session.user as Parameters<typeof handleUser>[0]);
        } else {
          onReady(false);
        }
      })
      .catch(() => {
        clearTimeout(timer);
        onReady(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        clearTimeout(timer);
        handleUser(session.user as Parameters<typeof handleUser>[0]);
        onReady(true);
      } else {
        handledRef.current = false;
        setUser(null);
        onReady(false);
      }
    });

    return () => {
      clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, []);

  return null;
}

function AppEffects() {
  const { lang, fontSize, highContrast, grayscale, readableFont, loadDemoData, darkMode } =
    useStore();
  const accounts = useStore((s) => s.accounts);
  const trades = useStore((s) => s.trades);
  const liveBalances = useStore((s) => s.liveBalances);
  const setAccountArchived = useStore((s) => s.setAccountArchived);

  // Auto-archive failed/completed prop accounts:
  //  - Eval (challenge): archive when breached OR passed (both end the account for the user)
  //  - Funded: archive when breached (burned). A passed funded account stays open.
  useEffect(() => {
    for (const acc of accounts) {
      if (acc.is_active === false) continue;
      const cat = categorizeAccount(acc);
      if (cat === 'live') continue;
      // Only accounts with a configured drawdown are tracked — without it calcPropFirmStats
      // falsely reports 'breached' (trailingFloor === highWaterMark when maxDD = 0).
      if ((acc.prop_max_drawdown ?? 0) <= 0) continue;
      const { status } = calcPropFirmStats(acc, trades, liveBalances?.[acc.id]);
      const shouldArchive =
        cat === 'eval' ? status === 'breached' || status === 'passed' : status === 'breached';
      if (shouldArchive) setAccountArchived(acc.id, true);
    }
  }, [accounts, trades, liveBalances, setAccountArchived]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
  }, [lang]);

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}%`;
  }, [fontSize]);

  useEffect(() => {
    document.body.style.filter = grayscale
      ? 'grayscale(100%)'
      : highContrast
        ? 'contrast(160%)'
        : '';
  }, [grayscale, highContrast]);

  useEffect(() => {
    document.documentElement.classList.toggle('readable-font', readableFont);
  }, [readableFont]);

  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
    // Keep mobile browser chrome / PWA status bar in sync with the theme
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', darkMode ? '#0e0b1f' : '#635bff');
  }, [darkMode]);

  useEffect(() => {
    if (DEMO_MODE) loadDemoData();
  }, []);

  return null;
}

function GatedRoute({ feature, children }: { feature: Feature; children: React.ReactNode }) {
  const { subscriptionStatus, trialEndsAt } = useStore();
  if (!canUse(feature, subscriptionStatus, trialEndsAt))
    return <UpgradePrompt feature={feature} variant="page" />;
  return <>{children}</>;
}

// Owner-only route guard (admin email). Used for Strategy Lab — gated until the
// YouTube transcript infra works from Vercel IPs. Non-owners → redirect to dashboard.
function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { user } = useStore();
  if (user?.email !== OWNER_EMAIL) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/* מסנכרן ברוקרים ברקע — בכניסה, כל שעה, ובחזרה מהסתר */
function BackgroundSync() {
  const { user, reloadFromCloud, lastUserId } = useStore();
  const { toasts, show, dismiss } = useToast();
  const didInitialSync = useRef<string | null>(null);
  const lastSyncAt = useRef<number>(0);
  const welcomePending = useRef(false);
  const shownReconnectToast = useRef(false);
  const [showTvReconnect, setShowTvReconnect] = useState(false);

  // Detect ?welcome=1 on mount (Whop post-checkout redirect) and clean URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('welcome')) {
      welcomePending.current = true;
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Show welcome toast once data has loaded (lastUserId set = loadDataInBackground done)
  useEffect(() => {
    if (!welcomePending.current || !lastUserId) return;
    welcomePending.current = false;

    const run = async () => {
      const status = useStore.getState().subscriptionStatus;
      if (status === 'active') {
        show('🎉 Your TraderYo Pro subscription is active! Welcome aboard.', 'success');
        return;
      }
      // Webhook may be slightly delayed — wait 3s and reload once
      await new Promise<void>((r) => setTimeout(r, 3000));
      await reloadFromCloud();
      const newStatus = useStore.getState().subscriptionStatus;
      if (newStatus === 'active') {
        show('🎉 Your TraderYo Pro subscription is active! Welcome aboard.', 'success');
      } else {
        show('Your subscription is activating — refresh the page in a moment.', 'info');
      }
    };
    run();
  }, [lastUserId]);

  const runSync = useCallback(async () => {
    if (!user || user.id === 'demo' || !supabase) return;
    const {
      data: { session },
    } = await supabase!.auth.getSession();
    const jwt = session?.access_token;
    if (!jwt) return;

    const { data: connections } = await supabase!
      .from('broker_connections')
      .select('broker, is_active, token_error')
      .eq('user_id', user.id);

    if (!connections?.length) return;

    if (TRADOVATE_SYNC_ENABLED && !shownReconnectToast.current) {
      const deadTradovate = connections.some(
        (c: { broker: string; is_active: boolean; token_error: string | null }) =>
          c.broker === 'tradovate' && !c.is_active && c.token_error,
      );
      if (deadTradovate && !localStorage.getItem('tv_reconnect_dismissed')) {
        shownReconnectToast.current = true;
        setShowTvReconnect(true);
      }
    }

    const activeConnections = connections.filter((c: { is_active: boolean }) => c.is_active);
    if (!activeConnections.length) return;

    const brokers = new Set(activeConnections.map((c: { broker: string }) => c.broker));
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string;
    const anonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string;

    const syncs: Promise<void>[] = [];

    if (TRADOVATE_SYNC_ENABLED && brokers.has('tradovate')) {
      syncs.push(
        fetch('/api/tradovate-sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ user_id: user.id }),
        })
          .then(async (r) => {
            if (!r.ok) return;
            const data = (await r.json()) as { errors?: string[] };
            const tokenDead = data.errors?.some(
              (e) => e.includes('expired') || e.includes('reconnect'),
            );
            if (tokenDead && !localStorage.getItem('tv_reconnect_dismissed'))
              setShowTvReconnect(true);
          })
          .catch(() => {}),
      );
    }

    if (brokers.has('topstepx')) {
      syncs.push(
        fetch(`${supabaseUrl}/functions/v1/topstepx-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: anonKey,
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ user_id: user.id }),
        })
          .then(() => {})
          .catch(() => {}),
      );
    }

    if (syncs.length) {
      await Promise.all(syncs);
      reloadFromCloud();
      lastSyncAt.current = Date.now();
    }
  }, [user?.id, show, reloadFromCloud]);

  // Initial sync — 3s after login to let data load first
  useEffect(() => {
    if (!user || user.id === 'demo' || !supabase || didInitialSync.current === user.id) return;
    didInitialSync.current = user.id;
    shownReconnectToast.current = false;
    const timer = setTimeout(runSync, 3000);
    return () => clearTimeout(timer);
  }, [user?.id, runSync]);

  // Periodic sync every 60 min — keeps Tradovate token alive (expires at 90 min)
  useEffect(() => {
    if (!user || user.id === 'demo' || !supabase) return;
    const interval = setInterval(runSync, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.id, runSync]);

  // Sync when tab regains visibility after > 5 min away
  useEffect(() => {
    if (!user || user.id === 'demo') return;
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastSyncAt.current > 5 * 60 * 1000) runSync();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user?.id, runSync]);

  // Realtime: detect Tradovate disconnect while user is in-app
  useEffect(() => {
    if (!TRADOVATE_SYNC_ENABLED) return;
    if (!user || user.id === 'demo' || !supabase) return;
    const channel = supabase
      .channel(`broker-status-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'broker_connections',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as {
            broker: string;
            is_active: boolean;
            token_error: string | null;
          };
          if (
            row.broker === 'tradovate' &&
            !row.is_active &&
            row.token_error &&
            !localStorage.getItem('tv_reconnect_dismissed')
          ) {
            shownReconnectToast.current = true;
            setShowTvReconnect(true);
          }
        },
      )
      .subscribe();
    return () => {
      supabase!.removeChannel(channel);
    };
  }, [user?.id]);

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
      <TradovateReconnectModal
        open={showTvReconnect}
        onClose={() => {
          setShowTvReconnect(false);
          shownReconnectToast.current = false;
        }}
      />
    </>
  );
}

function ProtectedRoute({
  children,
  ready,
  hasUser,
}: {
  children: React.ReactNode;
  ready: boolean;
  hasUser: boolean;
}) {
  const { user, onboardingDone, setOnboardingDone } = useStore();
  const isLoggedIn = hasUser || !!user;

  // dev preview: ?tour=1 forces the tour. Latched once — survives navigate() dropping the query.
  const devTourPreview = useRef(
    import.meta.env.DEV && new URLSearchParams(window.location.search).get('tour') === '1',
  ).current;

  // עדיין בודק session — הצג splash קצר
  if (!DEMO_MODE && !ready) return <SplashScreen />;

  // אין משתמש — עבור להתחברות
  if (!DEMO_MODE && !isLoggedIn) return <Navigate to="/auth" replace />;

  // יש משתמש — פתח מיד (נתונים יטענו ברקע)
  return (
    <>
      {children}
      {(devTourPreview || (!DEMO_MODE && isLoggedIn && !onboardingDone)) && (
        <ProductTour onDone={setOnboardingDone} />
      )}
    </>
  );
}

export default function App() {
  const [ready, setReady] = useState(DEMO_MODE);
  const [hasUser, setHasUser] = useState(false);

  return (
    <BrowserRouter>
      <AppEffects />
      <AuthListener
        onReady={(u) => {
          setReady(true);
          setHasUser(u);
        }}
      />
      <BackgroundSync />
      <CookieConsent />
      <InstallPrompt />
      <Suspense fallback={<SplashScreen />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/risk-disclosure" element={<RiskDisclosure />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/prop-firms" element={<PropFirms />} />
          <Route path="/prop-firms/:slug" element={<PropFirms />} />
          {/* /alternatives comparison pages unpublished for now — code kept in
              src/pages/Alternatives.tsx + src/lib/alternatives.ts. Re-enable by
              restoring these routes + the sitemap/footer entries. */}
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/share/:token" element={<PublicProfile />} />
          <Route path="/redeem" element={<Redeem />} />
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute ready={ready} hasUser={hasUser}>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="trades" element={<TradesList />} />
            <Route
              path="analytics"
              element={
                <GatedRoute feature="analytics">
                  <Analytics />
                </GatedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <GatedRoute feature="reports">
                  <Reports />
                </GatedRoute>
              }
            />
            <Route
              path="prop-hq"
              element={
                <GatedRoute feature="prop_hq">
                  <PropHQ />
                </GatedRoute>
              }
            />
            <Route path="propfirm" element={<Navigate to="/dashboard/prop-hq" replace />} />
            <Route path="business" element={<BusinessManager />} />
            <Route path="economic-calendar" element={<EconomicCalendar />} />
            <Route path="students" element={<Students />} />
            <Route path="settings" element={<Settings />} />
            <Route path="day-recap" element={<DayRecap />} />
            <Route path="coach" element={<Coach />} />
            <Route
              path="emotions"
              element={
                <OwnerRoute>
                  <EmotionJournal />
                </OwnerRoute>
              }
            />
            {/* Copier: standalone product. Owner-only until launch — then swap
                OwnerRoute for the page's copier_status self-gate. */}
            <Route
              path="copytrader"
              element={
                <OwnerRoute>
                  <CopyTrader />
                </OwnerRoute>
              }
            />
            <Route path="strategy-lab" element={<StrategyAnalyzer />} />
            <Route
              path="strategy-miner"
              element={
                <OwnerRoute>
                  <StrategyMiner />
                </OwnerRoute>
              }
            />
            <Route path="marketing" element={<MarketingAgent />} />
            <Route
              path="admin"
              element={
                <OwnerRoute>
                  <AdminDashboard />
                </OwnerRoute>
              }
            />
            <Route
              path="playbook"
              element={
                <GatedRoute feature="playbook">
                  <PlaybookLibrary />
                </GatedRoute>
              }
            />
            <Route
              path="playbook/:setupId"
              element={
                <GatedRoute feature="playbook">
                  <SetupDetail />
                </GatedRoute>
              }
            />
            <Route
              path="replay"
              element={
                <GatedRoute feature="trade_replay">
                  <ReplayRedirect />
                </GatedRoute>
              }
            />
            <Route
              path="replay/:tradeId"
              element={
                <GatedRoute feature="trade_replay">
                  <TradeReplay />
                </GatedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
